/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import MapView, { LocationMarker } from './Map/MapView';
import OrientationDataset from './utils/OrientationDataset';
import Header from './UI/Header';
import PointCloudWindow, { Facing } from './PCViewer/PointCloudWindow';
import { LoadablePointCloud } from './types/LoadablePointCloud';
import LoadPointCloudModal from './LoadPointCloudModal';
import { PhotoSphereTarget, formatPhotoSphereQuery, parsePhotoSphereQuery } from './utils/photoSphereTarget';

import { LngLat } from 'mapbox-gl';
import { Vector3 } from 'three';
import { message } from 'antd';

/** Beyond this distance the "nearest" photo sphere is probably not the one asked for. */
const PHOTO_SPHERE_MATCH_WARNING_METERS = 30;

/** Marker label for a look-at point that came without one (older Sign Review links). */
const LOOK_AT_MARKER_LABEL = 'Signpost location';

/** `<dir>/<chunk>` part of a point cloud URL, independent of the CDN domain. */
const pointCloudKey = (url: string) => url.split('/').slice(-2).join('/');

interface State {
    loadedPointCloud: LoadablePointCloud;
    leftPanelWith: number;
    pendingPcUrl: string | null;
    activeSatelliteView: boolean;
}

interface Props {
    getAccessTokenSilently: (options?: any) => Promise<string>;
    loginWithRedirect: (options?: any) => Promise<void>;
}


export default class AppManager extends Component<Props, State> {
    orientationDataset?: OrientationDataset;
    /** Every point cloud the load modal knows about, once it has fetched them. */
    availablePointClouds?: LoadablePointCloud[];
    /** Target requested before the point cloud list was available. */
    targetAwaitingPointClouds?: PhotoSphereTarget;
    /** Target to jump to once the point cloud being loaded is ready. */
    targetAfterLoad?: PhotoSphereTarget;
    /** The photo sphere being viewed, kept in the URL with its facing. */
    currentPhotoSphereIndex?: number;
    /** The settled facing of that photo sphere, kept when the URL is rewritten. */
    currentFacing?: Facing;
    /** The look-at point pinned on the map, kept in the URL until removed. */
    locationMarker?: LocationMarker;

    LIDAR_VIEW: React.RefObject<PointCloudWindow>;
    MAP_VIEW: React.RefObject<MapView>;
    CHOOSE_POINT_CLOUD_MODAL = React.createRef<LoadPointCloudModal>();

    constructor(props: Props) {
        super(props);

        this.state = {
            leftPanelWith: 65,
            pendingPcUrl: null,
            activeSatelliteView: true,
            loadedPointCloud: {
                url: `${process.env.REACT_APP_LIDAR_CDN_URL}/milton_ga_processed/07072025A_6440_001-002`,
                name: "Milton GA Processed",
                description: "Scan of Milton GA Processed",
                date_uploaded: new Date(`11/13/2023`),
                data_identifier: 'milton_ga',
                collector: 'NV5',
                projection: `+proj=lcc +lat_0=44.25 +lon_0=-109.5 +lat_1=45 +lat_2=49 +x_0=600000 +y_0=0 +datum=NAD83 +units=us-ft +no_defs +type=crs`,
            },

        };

        this.LIDAR_VIEW = React.createRef();
        this.MAP_VIEW = React.createRef();

        this.handleOnMouse = this.handleOnMouse.bind(this);
        this.onResize = this.onResize.bind(this);
    }

    toggleSatelliteView = () => {
        this.MAP_VIEW.current?.setSatelliteView(!this.state.activeSatelliteView);
    };

    componentDidMount() {
        this.flushAndSetupEventListeners();
        // Check if an'adapt' param was passed in
        const params = new URLSearchParams(window.location.search);
        let pcUrl: string | null = null;
        let pfParam: string | null = null;
        const adaptParam = params.get('adapt');
        if (adaptParam) {
            try {
                const decoded = JSON.parse(atob(adaptParam));
                pcUrl = decoded.pc || null;
                pfParam = decoded.pf || null;
            } catch (e) {
                console.error('Failed to decode adapt param:', e);
            }
        }
        console.log('pcUrl from URL param:', pcUrl);

        // A photo sphere opened from Asset Editor: ?ae=<dir>/<chunk> @ lat,lng
        const aeTarget = parsePhotoSphereQuery(params.get('ae') || '');
        if (aeTarget) {
            // Consume it so a refresh follows the adapt param syncUrlParam writes
            params.delete('ae');
            const search = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));

            // Resolve its point cloud once the modal has fetched the list
            this.targetAwaitingPointClouds = aeTarget;
            this.MAP_VIEW.current?.loadMap(() => {
                console.log('Map initialized, waiting for point cloud list...');
            });
        } else if (pcUrl) {
            // Store the pending URL — we'll resolve the full LoadablePointCloud
            // once the modal finishes loading all point clouds.
            // Initialize the map now so it's ready when the modal callback fires.
            this.setState({ pendingPcUrl: pcUrl });
            this.MAP_VIEW.current?.loadMap(() => {
                console.log('Map initialized, waiting for point cloud list...');
            });
        } else {
            // After setting the initial URL, load the point cloud
            this.MAP_VIEW.current?.loadMap(async () => {
                // When mapbox is finished initializing, load our point cloud.
                await this.loadPointCloud();

            });
        }


        document.addEventListener(
            'mouseup',
            () => {
                document.removeEventListener('mousemove', this.onResize);
            },
            false
        );
    }

    async componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>
    ): Promise<void> { }

    /**
     * @description Writes the `adapt` URL param: point cloud (pc), photo sphere
     * (ps), panorama file (pf) and, once the view has settled, its facing as
     * compass heading (h) and tilt (t) in degrees. A call without a facing
     * drops it, so a newly opened sphere starts from its own. A pinned
     * location marker is kept as `m` (`lat,lng`) and its label as `ml`.
     */
    syncUrlParam(url: string, photoSphereIndex?: number, panoFile?: string, facing?: Facing) {
        const params = new URLSearchParams(window.location.search);
        // Recover existing pf from the current 'adapt' param if not passed explicitly
        let pf = panoFile;
        if (!pf) {
            const existing = params.get('adapt');
            if (existing) {
                try { pf = JSON.parse(atob(existing)).pf; } catch (e) {
                    console.error('Failed to decode adapt param for pf:', e);
                 }
            }
        }
        const payload: Record<string, string> = { pc: url };
        if (pf) payload.pf = pf;
        if (photoSphereIndex !== undefined) payload.ps = String(photoSphereIndex);
        if (photoSphereIndex !== undefined && facing) {
            payload.h = String(facing.heading);
            payload.t = String(facing.tilt);
        }
        if (this.locationMarker) {
            payload.m = `${this.locationMarker.lat},${this.locationMarker.lng}`;
            // btoa only takes Latin-1; a label beyond it is left out
            if (!/[^\u0000-\u00ff]/.test(this.locationMarker.label)) payload.ml = this.locationMarker.label;
        }
        params.set('adapt', btoa(JSON.stringify(payload)));

        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }

    /**
     * @description Keeps the settled facing of the photo sphere view in the URL,
     * so a refresh or a shared link opens looking the same way.
     * @param facing The view's heading and tilt
     */
    handleFacingChange = (facing: Facing) => {
        if (this.currentPhotoSphereIndex === undefined) return;
        this.currentFacing = facing;
        this.syncUrlParam(this.state.loadedPointCloud.url, this.currentPhotoSphereIndex, undefined, facing);
    };

    /**
     * @description Pins the look-at point on the map, or removes the pin.
     * The caller writes the URL.
     * @param point The point, or undefined to remove it
     * @param label What the point is, e.g. `Sign 1184876 · Signpost 52345`
     */
    setLocationMarker(point?: { lat: number, lng: number }, label?: string) {
        this.locationMarker = point ?
            { lat: point.lat, lng: point.lng, label: label || LOOK_AT_MARKER_LABEL } :
            undefined;
        this.MAP_VIEW.current?.setLocationMarker(this.locationMarker);
    }

    handleRemoveLocationMarker = () => {
        this.setLocationMarker(undefined);
        const index = this.currentPhotoSphereIndex;
        this.syncUrlParam(this.state.loadedPointCloud.url, index, undefined, index !== undefined ? this.currentFacing : undefined);
    };

    loadPointCloudFromLoadablePointCloud = (pc: LoadablePointCloud) => {
        this.currentPhotoSphereIndex = undefined;
        this.currentFacing = undefined;
        // The marker belongs to the previous point cloud; a target with a
        // look-at pins its own once the new one is loaded.
        this.setLocationMarker(undefined);
        localStorage.setItem('data_identifier', pc.data_identifier || '')
        this.syncUrlParam(pc.url);
        this.setState({ loadedPointCloud: pc }, async () => {
            await this.loadPointCloud();
        });
    };

    handlePointCloudsLoaded = (pointClouds: LoadablePointCloud[]) => {
        this.availablePointClouds = pointClouds;

        if (this.targetAwaitingPointClouds) {
            const target = this.targetAwaitingPointClouds;
            this.targetAwaitingPointClouds = undefined;
            this.goToPhotoSphere(target);
            return;
        }

        const { pendingPcUrl } = this.state;
        if (!pendingPcUrl) return;

        // Find the matching point cloud by URL
        const match = pointClouds.find(pc => pc.url === pendingPcUrl);

        const pcToLoad = match || { ...this.state.loadedPointCloud, url: pendingPcUrl };

        this.setState({ pendingPcUrl: null, loadedPointCloud: pcToLoad }, async () => {
            // Map is already initialized from componentDidMount, load the point cloud directly
            await this.loadPointCloud();
        });
    };

    /**
     * @description Opens the photo sphere nearest to a target in the target's
     * point cloud, loading that point cloud first if it isn't the current one.
     * @param target The point cloud and position to open
     * @return {void}
     */
    goToPhotoSphere = (target: PhotoSphereTarget) => {
        const key = `${target.dir}/${target.chunk}`;
        const loadedUrl = this.state.loadedPointCloud.url;

        // Same point cloud and its photo spheres are ready: just jump
        if (
            pointCloudKey(loadedUrl) === key &&
            this.orientationDataset?.successfullyLoaded &&
            this.orientationDataset.url === loadedUrl + '/orientation.csv'
        ) {
            this.jumpToNearestPhotoSphere(target);
            return;
        }

        if (!this.availablePointClouds) {
            this.targetAwaitingPointClouds = target;
            message.info('Loading point cloud list...');
            return;
        }

        const pointCloud = this.availablePointClouds.find(pc => pointCloudKey(pc.url) === key);
        if (!pointCloud) {
            this.recoverMissingPointCloud(target);
            return;
        }

        this.targetAfterLoad = target;
        this.loadPointCloudFromLoadablePointCloud(pointCloud);
    };

    /**
     * @description A target's point cloud is missing from the list. The usual
     * cause is an expired session: the app still looks signed in (Auth0 caches
     * it in localStorage), but no token can be fetched, so every dataset's list
     * came back empty. Sign in again and come back to the same photo sphere;
     * with a valid session, fetch the list once more before giving up.
     * @param target The photo sphere that was asked for
     * @return {Promise<void>}
     */
    recoverMissingPointCloud = async (target: PhotoSphereTarget) => {
        const key = `${target.dir}/${target.chunk}`;
        try {
            await this.props.getAccessTokenSilently();
        } catch (error) {
            console.warn('No access token, signing in again:', error);
            message.info('Your session has expired. Signing in again…');
            const params = new URLSearchParams(window.location.search);
            params.set('ae', formatPhotoSphereQuery(target));
            await this.props.loginWithRedirect({
                appState: { returnTo: `${window.location.pathname}?${params.toString()}` },
            });
            return;
        }

        const hide = message.loading('Reloading point cloud list...', 0);
        const pointClouds = await this.CHOOSE_POINT_CLOUD_MODAL.current?.loadPointClouds();
        hide();
        const pointCloud = pointClouds?.find(pc => pointCloudKey(pc.url) === key);
        if (!pointCloud) {
            message.error(`Point cloud not found: ${key}`);
            return;
        }
        this.targetAfterLoad = target;
        this.loadPointCloudFromLoadablePointCloud(pointCloud);
    };

    /**
     * @description Views the photo sphere of the loaded dataset nearest to a target position.
     * @param target The position to match
     * @return {void}
     */
    jumpToNearestPhotoSphere = (target: PhotoSphereTarget) => {
        const lnglat = this.orientationDataset?.lnglat || [];
        // Equirectangular approximation, plenty for picking among nearby spheres
        const cosLat = Math.cos((target.lat * Math.PI) / 180);
        let nearestIndex = -1;
        let nearestDistance = Infinity;
        lnglat.forEach((point, index) => {
            const dx = (point.lng - target.lng) * cosLat;
            const dy = point.lat - target.lat;
            const distance = dx * dx + dy * dy;
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = index;
            }
        });

        if (nearestIndex === -1) {
            message.error('No photo spheres in this point cloud');
            return;
        }

        const meters = Math.sqrt(nearestDistance) * 111320;
        if (meters > PHOTO_SPHERE_MATCH_WARNING_METERS) {
            message.warning(`Nearest photo sphere is ${Math.round(meters)} m from the requested position`);
        }

        this.MAP_VIEW.current?.teleportTo(18, lnglat[nearestIndex]);
        if (target.look) this.setLocationMarker(target.look, target.label);
        // Face the point the other app asked about (a little down toward it,
        // see jumpToPhotoSphere)
        const sphere = this.orientationDataset?.photoSpheres[nearestIndex];
        const lookAt = target.look && sphere ?
            this.orientationDataset?.projectLngLatToWorld(target.look, sphere.camera_pos.z) || undefined :
            undefined;
        this.viewPhotoSphere(nearestIndex, lookAt);
    };

    /**
 * @description Flushes all our event listeners and adds them back so we don't have duplicate listeners.
 */
    flushAndSetupEventListeners() {
        // Grab our event listener div in the root.
        let eventListener = document.getElementById('event_listener');

        // Check if we have it
        if (eventListener) {
            // Flush Procedure time:

            // Clone the element
            const newListener = eventListener.cloneNode(true);
            // Replace it with the clone, removing all listeners
            eventListener.replaceWith(newListener);
            // Set our new lister as our event listener
            eventListener = newListener as HTMLElement;

            // Begin adding our events:

            eventListener.addEventListener('on-free-cam-moved', (e) => {
                // transform position and rotation to mapbox coordinates

                // Our custom even has a payload that TS isn't recognizing
                // @ts-ignore e.detail
                // @ts-ignore e.detail
                const position_override = e.detail.position;
                // @ts-ignore e.detail
                const rotation_override = e.detail.rotation;
                // @ts-ignore e.detail
                const cameraRotation = rotation_override;
                const cameraPosition = position_override;

                if (!cameraRotation || !cameraPosition) {
                    console.error(
                        'Failed to update camera rotation on mapbox map'
                    );
                    return;
                }
                let positiveAngle = cameraRotation.z * (180 / Math.PI);
                positiveAngle = positiveAngle % 360;
                positiveAngle =
                    positiveAngle > 0 ? positiveAngle : positiveAngle + 360;
                // Make sure our dataset has been initialized or we don't care
                if (this.orientationDataset) {
                    this.MAP_VIEW.current?.updateUserLocation(
                        cameraPosition
                        ,
                        360 - positiveAngle
                    );
                }

            });
        }
    }

    async loadPointCloud() {
        // Take the target now so it can't leak into a later load
        const target = this.targetAfterLoad;
        this.targetAfterLoad = undefined;

        // Decode any params from 'adapt' URL params
        const params = new URLSearchParams(window.location.search);
        let psValue: string | null = null;
        let pfValue: string | null = null;
        let facing: Facing | undefined;
        const adaptParam = params.get('adapt');
        if (adaptParam) {
            try {
                const decoded = JSON.parse(atob(adaptParam));
                psValue = decoded.ps || null;
                pfValue = decoded.pf || null;
                // Read before syncUrlParam below rewrites the param without it
                const heading = Number(decoded.h);
                const tilt = Number(decoded.t);
                if (decoded.h !== undefined && Number.isFinite(heading) && Number.isFinite(tilt)) {
                    facing = { heading, tilt };
                }
                const marker = typeof decoded.m === 'string' ? decoded.m.split(',').map(Number) : [];
                if (marker.length === 2 && marker.every(Number.isFinite)) {
                    this.setLocationMarker(
                        { lat: marker[0], lng: marker[1] },
                        typeof decoded.ml === 'string' ? decoded.ml : undefined,
                    );
                }
            } catch (e) { 
                console.error('Failed to decode adapt param:', e);
             }
        }

        const psIndex = psValue !== null ? parseInt(psValue, 10) : undefined;
        this.syncUrlParam(this.state.loadedPointCloud.url, !isNaN(psIndex as number) ? psIndex : undefined);
        // Wait until we finished loading our point cloud and get the pointcloud object
        await this.LIDAR_VIEW.current?.loadPointCloud(
            this.state.loadedPointCloud.url,
            this.state.loadedPointCloud.projection
        );
       
        
         try {
          const url = this.state.loadedPointCloud.url + '/orientation.csv'

        // Create a new orientation dataset object, this will store all our information needed for stitching photos to lidar
        const orientationDataset = new OrientationDataset({
            url: url,
            projection: this.state.loadedPointCloud.projection,
            collector: this.state.loadedPointCloud.collector,
            data_identifier: this.state.loadedPointCloud.data_identifier,
            getAccessTokenSilently: this.props.getAccessTokenSilently,
        });
        // Store this dataset on our AppManager object
        this.orientationDataset = orientationDataset;

        // Get the point cloud's bounding box for intersection testing
        const pointCloudBoundingBox = this.LIDAR_VIEW.current?.getPointCloudBoundingBox();
        // Load our dataset data in, passing the bounding box for intersection checking
        await orientationDataset.loadDataset(pointCloudBoundingBox, this.LIDAR_VIEW.current?.viewer.scene.pointclouds[0]);
        // Load our photospheres if such a dataset exists
        if (orientationDataset.successfullyLoaded) {
            // Make sure our map is showing
            window.dispatchEvent(new Event('resize'))
            // Add photospheres to lidar
            this.LIDAR_VIEW.current?.addPhotoSpheres(
                orientationDataset.getLocations()
            );

            setTimeout(() => {
                // Update our map with our new photospheres
                this.MAP_VIEW.current?.updatePhotoSpheres(
                    orientationDataset.getLngLat()
                );
            }, 1000);
            // Add photospheres to our mapbox map
            this.MAP_VIEW.current?.updatePhotoSpheres(orientationDataset.getLngLat());
        }
        if (target && orientationDataset.photoSpheres.length > 0) {
            this.jumpToNearestPhotoSphere(target);
            return;
        }

        // Start out map at the first photosphere

        if (orientationDataset.photoSpheres.length > 0) {
            if (this.state.loadedPointCloud.url === `https://citian-public-v2.s3.us-east-2.amazonaws.com/lidar-samples/minessota`) {
                this.MAP_VIEW.current?.teleportTo(
                    15,
                    new LngLat(-93.23981743470034, 44.98457493637079,)

                );
            } 
            else if (orientationDataset.collector === 'longbeach_collector') {
                // TODO: hard code long beach location until we get camera pos working
                this.MAP_VIEW.current?.teleportTo(
                    16,
                   orientationDataset.lnglat[0]
                );
                return;
            } else {
                let photoSphereIndex = psValue ? parseInt(psValue, 10) : 0;
              
                if (pfValue) {
                    const foundIndex = orientationDataset.photoSpheres.findIndex(
                        (s: any) => s.panorama_image.endsWith(pfValue)
                    );
                    if (foundIndex !== -1) {
                        photoSphereIndex = foundIndex;
                    }
                }
                    this.MAP_VIEW.current?.teleportTo(
                        18,
                        orientationDataset.projectWorldToLngLat(
                            orientationDataset.photoSpheres[photoSphereIndex].camera_pos
                        )
                    );
                    this.viewPhotoSphere(photoSphereIndex);
                    // Look the way the link was saved looking
                    if (facing) {
                        this.currentFacing = facing;
                        this.LIDAR_VIEW.current?.applyFacing(facing);
                    }

            }
        }
        // else if our lidar view even exists...
        else if (this.LIDAR_VIEW.current) {
            this.MAP_VIEW.current?.teleportTo(
                12,
                orientationDataset.projectWorldToLngLat(
                    this.LIDAR_VIEW.current.getPointCloudCenter()
                )
            );
        }
        } catch (error) {
            console.error('Error loading orientation dataset:', error);
            
        }
    }

    /**
 * @description Takes an index and changes our camera to view that photosphere including the panoramic skybox and everything.
 * @param index The index of our photosphere we want to set ourselves to view
 * @param lookAt A world position to face instead of the capture direction
 * @param keepFacing Keep the current facing when already viewing a sphere
 * @return {void}
 */
    viewPhotoSphere = (index: number, lookAt?: Vector3, keepFacing = false) => {
        if (!this.orientationDataset)
            return console.error(
                'Failed to load photosphere, no orientation dataset loaded!'
            );
        if (index >= this.orientationDataset.locations.length || index < 0)
            return console.error(
                'Failed to load photosphere, index out of bounds!'
            );

        console.debug(
            `Jumping to photosphere ${index} at`,
            this.orientationDataset.locations[index]
        );

        this.LIDAR_VIEW.current?.jumpToPhotoSphere(
            this.orientationDataset.photoSpheres[index],
            lookAt,
            keepFacing,
        );

        this.currentPhotoSphereIndex = index;
        this.syncUrlParam(this.state.loadedPointCloud.url, index);
    };

    onResize(e: any) {
        let newWidth = Math.round((e.x / document.body.clientWidth) * 10000) / 100.0

        // max width
        if (newWidth > 99.75) {
            newWidth = 99.75
        }

        // min width
        if (newWidth < 0.25) {
            newWidth = 0.25
        }

        this.setState(
            {
                leftPanelWith: newWidth,
            },

            // re-draw canvas
            () => window.dispatchEvent(new Event('resize'))
        );
    }

    handleOnMouse(e: any) {
        switch (e.type) {
            case 'mousedown':
                document.addEventListener('mousemove', this.onResize);
                break;
            default:
        }
    }

    render() {
        return (
            <div className="app">
                <Header
                    loadPointCloud={
                        this.loadPointCloudFromLoadablePointCloud
                    }
                    LIDAR_VIEW={this.LIDAR_VIEW}
                    getAccessTokenSilently={this.props.getAccessTokenSilently}
                />
                <div className="main-container">
                    <div 
                        id="left_panel" 
                        className="left_panel"
                        style={{
                            width: this.state.leftPanelWith + '%',
                        }}>
                        <PointCloudWindow
                            ref={this.LIDAR_VIEW}
                            mapRef={this.MAP_VIEW}
                            modalRef={this.CHOOSE_POINT_CLOUD_MODAL}
                            getAccessTokenSilently={this.props.getAccessTokenSilently}
                            data_identifier={this.state.loadedPointCloud.data_identifier || ''}
                            activeSatelliteView={this.state.activeSatelliteView}
                            toggleSatelliteView={this.toggleSatelliteView}
                            onFacingChange={this.handleFacingChange}
                        />
                    </div>

                    <div 
                        className="right_panel"
                        style={{
                            width: 100 - this.state.leftPanelWith + '%',
                        }}>
                        <div
                            id="resizer"
                            onMouseDown={this.handleOnMouse}
                        ></div>
                        <MapView
                            getAccessTokenSilently={
                                this.props.getAccessTokenSilently
                            }
                            loadedPointCloud={this.state.loadedPointCloud}
                            ref={this.MAP_VIEW}
                            LIDAR_VIEW={this.LIDAR_VIEW}
                            // Stepping to another sphere on the map keeps the facing
                            viewPhotoSphere={(index: number) => this.viewPhotoSphere(index, undefined, true)}
                            goToPhotoSphere={this.goToPhotoSphere}
                            onSatelliteViewChange={(show) => this.setState({ activeSatelliteView: show })}
                            onRemoveLocationMarker={this.handleRemoveLocationMarker}
                        />
                    </div>
                </div>

                <LoadPointCloudModal
                    getAccessTokenSilently={this.props.getAccessTokenSilently}
                    ref={this.CHOOSE_POINT_CLOUD_MODAL}
                    onLoad={this.loadPointCloudFromLoadablePointCloud}
                    onPointCloudsLoaded={this.handlePointCloudsLoaded}
                />
            </div>
        );
    }
}
